import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";

type View = "year" | "month" | "day";

export interface DobPickerProps {
  value?: string | null;
  onChange: (iso: string) => void;
  minYear?: number;
  maxYear?: number;
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function getDaysInMonth(year: number, monthIndex: number) {
  return new Date(year, monthIndex + 1, 0).getDate();
}

export default function DobPicker({
  value,
  onChange,
  minYear,
  maxYear,
}: DobPickerProps) {
  const today = new Date();
  const defaultMax = today.getFullYear() - 13;
  const defaultMin = today.getFullYear() - 100;
  const low = minYear ?? defaultMin;
  const high = maxYear ?? defaultMax;

  // derive selection from value if ISO
  const initial = useMemo(() => {
    if (!value) return { y: undefined as number | undefined, m: undefined as number | undefined };
    const parts = value.split("-");
    if (parts.length === 3) {
      const y = Number(parts[0]);
      const m = Number(parts[1]) - 1;
      if (!Number.isNaN(y) && !Number.isNaN(m)) return { y, m };
    }
    return { y: undefined as number | undefined, m: undefined as number | undefined };
  }, [value]);

  const [view, setView] = useState<View>(initial.y != null ? (initial.m != null ? "day" : "month") : "year");
  const [year, setYear] = useState<number | undefined>(initial.y);
  const [month, setMonth] = useState<number | undefined>(initial.m);

  // Year paging (12 per page)
  const initialPageStart = Math.floor((clamp((year ?? high), low, high) - low) / 12) * 12 + low;
  const [pageStart, setPageStart] = useState<number>(initialPageStart);
  const years = Array.from({ length: 12 }, (_, i) => pageStart + i).filter(y => y <= high && y >= low);

  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  return (
    <div className="w-[320px] sm:w-[360px] p-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => {
          if (view === "day") setView("month");
          else if (view === "month") setView("year");
          else setPageStart(prev => clamp(prev - 12, low, high - 11));
        }}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="text-sm font-medium text-white">
          {view === "year" && `${years[0]}–${years[years.length - 1]}`}
          {view === "month" && (year ?? "Year")}
          {view === "day" && `${months[month ?? 0]} ${year}`}
        </div>
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => {
          if (view === "year") setPageStart(prev => clamp(prev + 12, low, high - 11));
          else if (view === "month") setView("day");
          else if (view === "day") setView("day");
        }}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Body */}
      {view === "year" && (
        <div className="grid grid-cols-3 gap-2">
          {years.map((y) => (
            <button
              key={y}
              type="button"
              className={cn(
                "h-10 rounded-md border text-white border-primary/20 hover:border-primary/40 transition",
                y === year && "bg-primary text-black shadow-neon-glow"
              )}
              onClick={() => {
                setYear(y);
                setView("month");
              }}
            >
              {y}
            </button>
          ))}
        </div>
      )}

      {view === "month" && (
        <div className="grid grid-cols-3 gap-2">
          {months.map((label, idx) => (
            <button
              key={label}
              type="button"
              className={cn(
                "h-10 rounded-md border text-white border-primary/20 hover:border-primary/40 transition",
                idx === month && "bg-primary text-black shadow-neon-glow"
              )}
              onClick={() => {
                setMonth(idx);
                setView("day");
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {view === "day" && year != null && month != null && (
        <div className="grid grid-cols-7 gap-2">
          {Array.from({ length: getDaysInMonth(year, month) }, (_, i) => i + 1).map((d) => (
            <button
              key={d}
              type="button"
              className="h-10 rounded-md border text-white border-primary/20 hover:border-primary/40 transition"
              onClick={() => {
                const date = new Date(year, month, d);
                onChange(format(date, "yyyy-MM-dd"));
              }}
            >
              {d}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}



